'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'

import { CartContainer } from '../../brunswick/containers/cart'
import { useGoogleTagManager } from '../../lib/hooks/useGoogleTagManager'

import { useLocalization } from './LocalizationProvider.client'

const useCartDialogImpl = () => {
  const { cart } = CartContainer.useContainer()
  const { region } = useLocalization()
  const { pushOpenMiniCart } = useGoogleTagManager()
  const [isOpen, setIsOpen] = useState(false)

  const pathname = usePathname()
  const previousPathnameRef = useRef(pathname)
  const pathnameChanged = previousPathnameRef.current !== pathname
  previousPathnameRef.current = pathname

  const searchParams = useSearchParams()
  const previousSearchParamsRef = useRef(searchParams)
  const searchParamsChanged = previousSearchParamsRef.current !== searchParams
  previousSearchParamsRef.current = searchParams

  // This openingRef should not be necessary.
  // Cypress moves so darn fast it sometimes opens a model quicker than React updates a query param that should have been updated prior to opening.
  // Example, pick a size on PDP, click add to bag. Size query param updates after bag starts opening. Breaking the test.
  const openingRef = useRef(false)

  if (pathnameChanged || (searchParamsChanged && !openingRef.current)) {
    setIsOpen(false)
  }

  const close = useCallback(() => {
    openingRef.current = false
    setIsOpen(false)
  }, [setIsOpen])

  const open = useCallback(() => {
    openingRef.current = true
    setIsOpen(true)
    pushOpenMiniCart(cart, region.currency)

    setTimeout(() => {
      openingRef.current = false
    }, 600)
  }, [cart, pushOpenMiniCart, region.currency])

  return {
    isOpen,
    close,
    open,
  }
}

const CartDialogContainer = createContainer(useCartDialogImpl)
CartDialogContainer.Provider.displayName = 'CartDialogProviderImpl'

const useCartInterstitialDialogImpl = () => {
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const previousPathnameRef = useRef(pathname)
  const previousSearchParamsRef = useRef(searchParams)

  if (
    previousPathnameRef.current !== pathname ||
    previousSearchParamsRef.current !== searchParams
  ) {
    previousPathnameRef.current = pathname
    previousSearchParamsRef.current = searchParams
    setIsOpen(false)
  }

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const open = useCallback(() => {
    setIsOpen(true)
  }, [])

  return {
    isOpen,
    close,
    open,
  }
}

const CartInterstitialDialogContainer = createContainer(useCartInterstitialDialogImpl)
CartInterstitialDialogContainer.Provider.displayName = 'CartInterstitialDialogProviderImpl'

export const CartProvider = (props: { children?: React.ReactNode }) => {
  return (
    <CartContainer.Provider>
      <CartDialogContainer.Provider>
        <CartInterstitialDialogContainer.Provider>
          {props.children}
        </CartInterstitialDialogContainer.Provider>
      </CartDialogContainer.Provider>
    </CartContainer.Provider>
  )
}

export const useCartDialog = CartDialogContainer.useContainer
export const useCartInterstitialDialog = CartInterstitialDialogContainer.useContainer
export const useCart = CartContainer.useContainer
